import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache for content to avoid repeated API calls
const contentCache = {};

/**
 * Hook to fetch editable site content
 * @param {string} key - Content key (e.g., 'homepage_hero_title')
 * @param {string} defaultValue - Default value if content not found
 * @returns {string} The content value
 */
export const useSiteContent = (key, defaultValue = '') => {
  const [content, setContent] = useState(contentCache[key] || defaultValue);

  useEffect(() => {
    if (contentCache[key]) {
      setContent(contentCache[key]);
      return;
    }

    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/content/${key}`);
        const value = res.data.content || defaultValue;
        contentCache[key] = value;
        setContent(value);
      } catch (error) {
        console.error(`Error fetching content for key ${key}:`, error);
        setContent(defaultValue);
      }
    };

    fetchContent();
  }, [key, defaultValue]);

  return content;
};

/**
 * Hook to fetch multiple content items at once
 * @param {Array<{key: string, defaultValue: string}>} items - Array of content items
 * @returns {Object} Object with content values keyed by their keys
 */
export const useSiteContentBatch = (items) => {
  const [contents, setContents] = useState(() => {
    const initial = {};
    items.forEach(item => {
      initial[item.key] = contentCache[item.key] || item.defaultValue;
    });
    return initial;
  });

  useEffect(() => {
    const fetchAllContent = async () => {
      try {
        const res = await axios.get(`${API}/content`);
        const allContent = res.data;
        
        const newContents = {};
        items.forEach(item => {
          const found = allContent.find(c => c.key === item.key);
          const value = found?.content || item.defaultValue;
          contentCache[item.key] = value;
          newContents[item.key] = value;
        });
        
        setContents(newContents);
      } catch (error) {
        console.error('Error fetching batch content:', error);
      }
    };

    fetchAllContent();
  }, []);

  return contents;
};

/**
 * Clear the content cache (useful after admin updates)
 */
export const clearContentCache = () => {
  Object.keys(contentCache).forEach(key => delete contentCache[key]);
};

export default useSiteContent;
